/**
 * P3 손실 구조 분석 API client.
 *
 * 단일 호출 GET /api/loss-structure/summary — 모든 섹션 데이터를 한 번에 받음.
 */
import { apiFetch } from '../utils/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

export interface LossKpis {
  trade_count: number;
  wins: number;
  losses: number;
  zeros: number;
  win_rate_realized: number;
  realized_pl: number;
  unrealized_pl: number;
  risk_reward_ratio: number | null;
}

export interface TradeTypeRow {
  trade_type: string;
  count: number;
  avg_percent: number;
  sum_amount: number;
  min_percent: number;
  max_percent: number;
}

export interface SymbolRow {
  symbol: string;
  count: number;
  sum_amount: number;
  avg_percent: number;
  has_loss_history: boolean;
}

export interface HoldPeriodRow {
  trade_type: string;
  count: number;
  avg_days: number;
  min_days: number;
  max_days: number;
}

export interface SignalBin {
  bin: string;
  count: number;
  wins: number;
  losses: number;
  sum_amount: number;
}

export interface SignalStrengthBlock {
  source: 'reason_text' | 'decision_record';
  no_match_count: number;
  bins: SignalBin[];
}

export interface OpenPositionRow {
  symbol: string;
  qty: number | null;
  asset_type: string | null;
  market_value: number | null;
  unrealized_pl: number | null;
  unrealized_pl_percent: number | null;
  entry_time: string | null;
  days_held: number | null;
}

export interface OpenPositionsBlock {
  report_date: string | null;
  positions: OpenPositionRow[];
}

export interface StrategyRow {
  strategy: string;
  buy_votes: number;
  sell_votes: number;
  hold_votes: number;
  total: number;
}

export interface StrategyDriftBlock {
  sample_size: number;
  strategies: StrategyRow[];
  note: string;
}

export interface VerificationItem {
  verified: boolean;
  sample_count?: number;
  nonzero_count?: number;
  note: string;
}

export interface VerificationBlock {
  v1_buy_decision_records: VerificationItem;
  v2_strategy_sell_profit_amount: VerificationItem;
}

export interface SellAnalysisEffective {
  buy_threshold: number;
  sell_threshold: number;
  source: string;
  default_buy_threshold: number;
  default_sell_threshold: number;
}

export interface SellAnalysisSample {
  all_decisions: number;
  sell_eligible_decisions: number;
  actual_strategy_sells: number;
  sample_gate: number;
}

export interface SellDistributionRow {
  bin: string;
  all_count: number;
  eligible_count: number;
  hold_count: number;
  sell_count: number;
}

export interface SellSensitivityRow {
  threshold: number;
  is_effective: boolean;
  would_sell_count: number;
  delta_vs_effective: number;
  swing_holds: number;
  prevented_sells: number;
  pnl_available: boolean;
  note: string;
}

export interface SellOutcomeRow {
  bin: string;
  count: number;
  avg_profit_percent: number;
  sum_amount: number;
  win_rate: number;
  sample_ok: boolean;
}

export interface SellAnalysisBlock {
  effective: SellAnalysisEffective;
  sample: SellAnalysisSample;
  distribution: SellDistributionRow[];
  sensitivity: SellSensitivityRow[];
  actual_outcomes: SellOutcomeRow[];
  notes: string[];
}

export interface Layer1SectorRow {
  sector: string;
  position_count?: number;
  symbols?: string[];
  trade_count?: number;
  stop_loss_count?: number;
  realized_pl?: number;
  avg_profit_percent?: number;
}

export interface Layer1SectorExposure {
  source: string;
  current_position_count: number;
  unknown_current_count: number;
  trade_count: number;
  unknown_trade_count: number;
  current: Layer1SectorRow[];
  realized: Layer1SectorRow[];
}

export interface StopLossSlippageRow {
  symbol: string;
  profit_percent: number;
  profit_amount: number;
  slippage_percent: number;
  executed_at: string | null;
}

export interface StopLossSlippage {
  baseline_stop_percent: number;
  sample_count: number;
  worse_than_baseline_count: number;
  avg_slippage_percent: number;
  worst_slippage_percent: number;
  worst_examples: StopLossSlippageRow[];
}

export interface StrategySellMinHold {
  min_hold_minutes: number;
  blocked_count: number;
  executed_sell_count: number;
  note: string;
}

export interface Layer1ShadowCandidate {
  key: string;
  label: string;
  status: 'measurable' | 'partial' | 'insufficient';
  would_block_count: number;
  affected_symbols: string[];
  risk_reduced_hint: string;
  order_path_changed: boolean;
  details: Record<string, unknown>;
}

export interface Layer1ShadowEvaluation {
  mode: 'shadow_only';
  sample_count: number;
  candidates: Layer1ShadowCandidate[];
  notes: string[];
}

export interface Layer1ReadinessBlock {
  order_path_changed: boolean;
  sector_exposure: Layer1SectorExposure;
  stop_loss_slippage: StopLossSlippage;
  strategy_sell_min_hold: StrategySellMinHold;
  shadow_evaluation: Layer1ShadowEvaluation;
  notes: string[];
}

export interface LossStructureSummary {
  generated_at: string;
  kpis: LossKpis;
  by_trade_type: TradeTypeRow[];
  by_symbol: SymbolRow[];
  hold_period: HoldPeriodRow[];
  signal_strength: SignalStrengthBlock;
  open_positions: OpenPositionsBlock;
  strategy_drift: StrategyDriftBlock;
  verification: VerificationBlock;
  sell_analysis: SellAnalysisBlock;
  layer1_readiness: Layer1ReadinessBlock;
}

export async function fetchLossStructureSummary(): Promise<LossStructureSummary> {
  const url = `${API_BASE_URL}/api/loss-structure/summary`;
  const response = await apiFetch(url);
  if (!response.ok) {
    throw new Error(`손실 구조 분석 조회 실패: ${response.status}`);
  }
  return response.json();
}
